import { useEffect, useState } from 'react';
import type { AvatarVariant } from '@schemer/shared';
import { api, ApiError } from '../lib/api.js';
import { AvatarPicker } from './AvatarPicker.js';
import { nameStore, tokenStore } from '../lib/storage.js';
import { navigate } from '../lib/route.js';

// The join screen doubles as "smart return": when opened via an invite link
// (#/j/CODE) it first checks whether this device is ALREADY a member of that
// game. If so, it resumes you straight into the game (or lobby) instead of the
// join form — so the same shared link works both before AND after the game has
// started. Non-members hitting a started game get a clear message, not a raw
// error.
type Phase = 'checking' | 'form' | 'blocked';

export function JoinScreen({
  prefilledCode,
}: {
  prefilledCode?: string | undefined;
}): React.JSX.Element {
  const [code, setCode] = useState((prefilledCode ?? '').toUpperCase());
  const [displayName, setDisplayName] = useState(nameStore.get() ?? '');
  const [avatar, setAvatar] = useState<AvatarVariant | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Probe the game when arriving via an invite link; bare #/j goes to the form.
  const [phase, setPhase] = useState<Phase>(prefilledCode ? 'checking' : 'form');

  useEffect(() => {
    if (!prefilledCode) return;
    let cancelled = false;
    void (async () => {
      try {
        const res = await api.getLobby(prefilledCode);
        if (cancelled) return;
        if (res.selfPlayerId) {
          // Already a member of this game → resume where it is now.
          navigate(
            res.lobby.status === 'lobby'
              ? { name: 'lobby', code: res.lobby.code }
              : { name: 'game', code: res.lobby.code },
          );
          return;
        }
        // Not (yet) a member: joinable only while still in the lobby.
        setPhase(res.lobby.status === 'lobby' ? 'form' : 'blocked');
      } catch {
        // Unknown code / offline → let them use the form (maybe a typo).
        if (!cancelled) setPhase('form');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [prefilledCode]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const existingToken = tokenStore.get();
      const res = await api.joinGame(code.trim().toUpperCase(), {
        displayName: displayName.trim(),
        ...(avatar ? { avatar } : {}),
        ...(existingToken ? { deviceToken: existingToken } : {}),
      });
      tokenStore.set(res.deviceToken);
      nameStore.set(displayName.trim());
      navigate({ name: 'lobby', code: res.lobby.code });
    } catch (err) {
      // If the game already started, we might still be a returning member —
      // check and resume instead of showing a dead-end error.
      if (err instanceof ApiError && err.code === 'game_not_in_lobby') {
        try {
          const res = await api.getLobby(code.trim().toUpperCase());
          if (res.selfPlayerId) {
            navigate({ name: 'game', code: res.lobby.code });
            return;
          }
        } catch {
          /* fall through to the message */
        }
        setError('Dit spel is al begonnen — je kunt niet meer meedoen.');
        return;
      }
      setError(err instanceof ApiError ? err.message || err.code : 'er ging iets mis');
    } finally {
      setSubmitting(false);
    }
  }

  if (phase === 'checking') {
    return (
      <main className="form-screen">
        <p className="subtitle">even kijken…</p>
      </main>
    );
  }

  if (phase === 'blocked') {
    return (
      <main className="form-screen">
        <button className="back" onClick={() => navigate({ name: 'splash' })}>
          ← terug
        </button>
        <h1>Meedoen met een spel</h1>
        <p className="bad">Dit spel is al begonnen — je kunt niet meer meedoen.</p>
      </main>
    );
  }

  return (
    <main className="form-screen">
      <button className="back" onClick={() => navigate({ name: 'splash' })}>
        ← terug
      </button>
      <h1>Meedoen met een spel</h1>
      <form onSubmit={onSubmit}>
        <label>
          Spelcode
          <input
            type="text"
            className="code-input"
            value={code}
            // 14 — tolerate a pasted invite link or spaced code: keep only the
            // characters the code alphabet uses (0/O/1/I/L are excluded there).
            onChange={(e) =>
              setCode(
                e.target.value
                  .toUpperCase()
                  .replace(/.*\//, '')
                  .replace(/[^A-Z2-9]/g, '')
                  .slice(0, 6),
              )
            }
            // No maxLength: it would truncate a pasted invite link before the
            // handler above can pull the code out of it. The slice(0, 6) there
            // enforces the length instead.
            pattern="[A-Z2-9]{6}"
            inputMode="text"
            autoCapitalize="characters"
            required
            autoFocus={!prefilledCode}
          />
        </label>
        <label>
          Jouw naam
          <input
            type="text"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            maxLength={24}
            required
            autoFocus={!!prefilledCode}
          />
        </label>
        <AvatarPicker value={avatar} onChange={setAvatar} disabled={submitting} />
        {error && <p className="bad">{error}</p>}
        <button
          className="cta primary"
          type="submit"
          disabled={submitting || code.length !== 6 || !displayName.trim()}
        >
          {submitting
            ? 'meedoen…'
            : code.length !== 6
              ? 'vul de spelcode in'
              : !displayName.trim()
                ? 'vul je naam in'
                : 'meedoen'}
        </button>
      </form>
    </main>
  );
}
